import React from 'react';
import { Box, IconButton, Typography, Chip, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export default function PrismProtocolSidebar({ open, protocol, onClose, onStartGuide }) {
  if (!open || !protocol) return null;

  return (
    <>
      {/* Overlay/Backdrop */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(2px)',
          zIndex: 9998,
          opacity: open ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '440px',
          height: '100vh',
          background: 'linear-gradient(165deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 27, 52, 0.97) 35%, rgba(42, 36, 65, 0.97) 70%, rgba(52, 45, 78, 0.97) 100%)',
          backdropFilter: 'blur(40px) saturate(150%)',
          WebkitBackdropFilter: 'blur(40px) saturate(150%)',
          color: 'white',
          boxShadow: '-12px 0 64px rgba(0, 0, 0, 0.6), -4px 0 24px rgba(162, 134, 201, 0.15), inset 1px 0 2px rgba(255, 255, 255, 0.1)',
          borderLeft: '1px solid rgba(162, 134, 201, 0.4)',
          zIndex: 9999,
          overflowY: 'auto',
          overflowX: 'hidden',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.2)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, rgba(162, 134, 201, 0.6) 0%, rgba(101, 169, 196, 0.6) 100%)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(180deg, rgba(162, 134, 201, 0.8) 0%, rgba(101, 169, 196, 0.8) 100%)',
          },
        }}
      >
        {/* Floating particles background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            opacity: 0.4,
            overflow: 'hidden',
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: `${120 + i * 40}px`,
                height: `${120 + i * 40}px`,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(162, 134, 201, 0.15) 0%, transparent 70%)',
                top: `${20 + i * 30}%`,
                left: `${-20 + i * 20}%`,
                animation: `float${i} ${20 + i * 5}s infinite ease-in-out`,
                '@keyframes float0': {
                  '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                  '50%': { transform: 'translate(30px, -30px) scale(1.1)' },
                },
                '@keyframes float1': {
                  '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                  '50%': { transform: 'translate(-20px, 40px) scale(0.9)' },
                },
                '@keyframes float2': {
                  '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                  '50%': { transform: 'translate(25px, 25px) scale(1.05)' },
                },
              }}
            />
          ))}
        </Box>

        {/* Content */}
        <Box sx={{ p: '32px 28px', position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3.5,
              p: '20px 24px',
              background: 'linear-gradient(135deg, rgba(162, 134, 201, 0.25) 0%, rgba(101, 169, 196, 0.20) 50%, rgba(162, 134, 201, 0.25) 100%)',
              borderRadius: '20px',
              border: '1px solid rgba(162, 134, 201, 0.4)',
              boxShadow: '0 8px 32px rgba(162, 134, 201, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.15)',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', mb: 0.5 }}>
                Protocol Guide
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                Interactive Workflow
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s',
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Protocol Title */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: 1.3,
                mb: 1.5,
                background: 'linear-gradient(135deg, rgba(162, 134, 201, 1) 0%, rgba(101, 169, 196, 1) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {protocol.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={protocol.category}
                size="small"
                sx={{
                  bgcolor: 'rgba(162, 134, 201, 0.2)',
                  color: 'rgba(162, 134, 201, 1)',
                  border: '1px solid rgba(162, 134, 201, 0.4)',
                  fontWeight: 600,
                }}
              />
              {protocol.tags?.slice(0, 3).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(101, 169, 196, 0.15)',
                    color: 'rgba(101, 169, 196, 1)',
                    border: '1px solid rgba(101, 169, 196, 0.3)',
                  }}
                />
              ))}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.6,
                fontSize: '14px',
              }}
            >
              {protocol.snippet}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 3 }} />

          {/* Protocol Steps */}
          {protocol.steps && protocol.steps.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  mb: 2,
                  color: 'rgba(162, 134, 201, 1)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                Protocol Steps ({protocol.steps.length})
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.5 } }}>
                {protocol.steps.map((step, index) => (
                  <li key={index}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        lineHeight: 1.6,
                        fontSize: '13px',
                      }}
                    >
                      {step}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}

          {/* Start Guide Button */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              pt: 2,
              pb: 1,
              background: 'linear-gradient(to top, rgba(15, 23, 42, 1) 0%, rgba(15, 23, 42, 0.95) 50%, transparent 100%)',
            }}
          >
            <Box
              onClick={() => {
                onStartGuide(protocol);
                onClose();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                p: 2,
                bgcolor: 'rgba(91, 158, 130, 0.2)',
                border: '2px solid rgba(91, 158, 130, 0.5)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: 'rgba(91, 158, 130, 0.3)',
                  border: '2px solid rgba(91, 158, 130, 0.7)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(91, 158, 130, 0.3)',
                },
              }}
            >
              <PlayArrowIcon sx={{ color: 'rgba(91, 158, 130, 1)', fontSize: 24 }} />
              <Typography
                variant="button"
                sx={{
                  fontWeight: 700,
                  fontSize: '15px',
                  color: 'rgba(91, 158, 130, 1)',
                  textTransform: 'none',
                }}
              >
                Start Interactive Guide
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 1.5,
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '11px',
              }}
            >
              Ghost paths will point to EMR elements
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
